import { forwardRef, PropsWithChildren, useContext } from 'react';
import { IconType } from '@robocorp/icons';
import { styled } from '@robocorp/theme';
import { Divider } from '@robocorp/components';
import { ListContext } from './List';
import ListContent from './ListContent';

export interface ListItemProps {
  /**
   * @ignore
   */
  id?: string;
  /**
   * @ignore
   */
  className?: string;
  /**
   * Item icon
   */
  icon?: IconType;
  /**
   * List item as a link
   */
  href?: string;
  /**
   * Show a label as list item content
   */
  label?: string;
  /**
   * Click event callback
   * */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Make list item dense
   */
  dense?: boolean;
  /**
   * Should list item have hover state
   */
  hover?: boolean;
  /**
   * Should list item have active state
   */
  active?: boolean;

  /**
   * Additional list item left padding offset
   */
  offset?: number;
}

const listElements = [
  styled.li<ListItemProps>``,
  styled.button<ListItemProps>``,
  styled.a<ListItemProps>``,
];

const ListItemsStyled = listElements.map(
  (el) => styled(el)<ListItemProps & { button: boolean }>`
    list-style: none;
    display: flex;
    align-items: center;
    background: none;
    width: 100%;
    text-align: left;
    padding: ${({ dense, offset }) =>
      dense
        ? `8px 16px 8px ${offset !== undefined ? 16 + offset : 16}px`
        : `12px 20px 12px ${offset !== undefined ? 20 + offset : 20}px`};
    min-height: ${({ dense }) => (dense ? '44px' : '60px')};
    color: ${({ theme }) => theme.colors.content.primary.color};

    .hover {
      opacity: 0;
      pointer-events: none;
    }

    ${({ button, hover, theme }) =>
      (button || hover) &&
      `
    &:hover {
      background: ${theme.colors.background.subtle.light.hovered.color};
    }
  `}

    &:hover .hover {
      opacity: 1;
      pointer-events: all;
    }

    ${({ button }) =>
      button &&
      `
    user-select: none;
    &:hover {
      cursor: pointer;
    }
  `}

    ${({ active, theme }) =>
      active &&
      `
    background: ${theme.colors.background.subtle.light.hovered.color};

    &:after {
      display: block;
      flex: 0 0 7px;
      border-radius: 50%;
      content: '';
      height: 7px;
      background: ${theme.colors.yellow50};
      margin-left: 8px;
    }
  `}

  > * + * {
      margin-left: 12px;
    }
  `,
);

/**
 * List item component
 */
const ListItem = forwardRef<HTMLLIElement, PropsWithChildren<ListItemProps>>(
  ({ children, icon, label, onClick, active, href, ...rest }, listItemRef) => {
    const { divider, dense, hover } = useContext(ListContext);
    const Icon = icon;

    const [StyledLi, StyledButton, StyledLink] = ListItemsStyled;

    let ListItemStyled = typeof onClick === 'function' ? StyledButton : StyledLi;

    if (href) {
      ListItemStyled = StyledLink;
    }

    return (
      <>
        <ListItemStyled
          button={typeof onClick === 'function' || typeof href === 'string'}
          onClick={onClick}
          href={href}
          dense={dense}
          hover={hover}
          active={active}
          ref={listItemRef}
          {...rest}
        >
          {Icon && (
            <ListContent type="icon">
              <Icon size={20} />
            </ListContent>
          )}
          {label && <ListContent type="text">{label}</ListContent>}
          {children}
        </ListItemStyled>
        {divider && <Divider />}
      </>
    );
  },
);

export default ListItem;
